import React from "react";

import {
  Warning as WarningIcon,
  FiberManualRecord as FiberManualRecordIcon,
} from "@mui/icons-material";
import { Box, Theme, Typography } from "@mui/material";
import { makeStyles, withStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import { Gender } from "@/gql/graphql";
import { colors } from "@/theme/overrides/colors";
import { humanize } from "@/utils/string";
import { FragmentType } from "@/utils/type";

import AllergenFoodIcon from "../Icons/AllergenFoodIcon";
import AllergenFoodWarningIcon from "../Icons/AllergenFoodIconWarning";

interface Props {
  data: FragmentType<typeof allergenIngredientListItemFragment>;
  client:
    | FragmentType<typeof allergenIngredientListItemClientFragment>
    | undefined;
  warning?: boolean;
}

const MNRCol = withStyles(Box, {
  root: {
    minWidth: 90,
    display: "flex",
    flexDirection: "column",
    gap: 5,
  },
});

const MNValue = withStyles(Typography, {
  root: {
    fontWeight: 700,
  },
});

const MNLabel = withStyles(Typography, {
  root: {
    fontWeight: 600,
    color: "#595F68",
  },
});

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 30,
    padding: "12px 0px 12px 10px",
  },
  rootWarning: {
    backgroundColor: "#ffe7e7",
  },
  name: {
    fontWeight: 400,
  },
  amount: {
    fontWeight: 600,
    color: "#595F68",
  },
  divider: {
    height: 1,
    backgroundColor: "#595F68",
    opacity: 0.2,
  },
  dotDivider: {
    marginTop: 2,
    width: 7,
    height: 7,
  },
  warning: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    padding: 5,
    maxWidth: 400,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: "#FF6262",
  },
  warningLogo: {
    width: 12,
    height: 12,
  },
  warningIndicator: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "5px 8px 5px 8px",
    backgroundColor: "#FF6262",
    borderRadius: 24,
  },
  warningIndicatorText: {
    fontSize: 8,
    fontWeight: 600,
    color: colors.white,
  },
  warningDescription: {
    fontSize: 10,
    fontWeight: 700,
  },
  activeAllergen: {
    marginTop: 4,
    display: "flex",
    alignItems: "center",
  },
  activeAllergenText: {
    fontWeight: 700,
    fontSize: 10,
  },
}));

const allergenIngredientListItemFragment = graphql(`
  fragment AllergenIngredientListItem on Ingredient {
    id
    food {
      id
      name
      epWeightGrams
      energyKcal
      carbohydrateGrams
      proteinGrams
      fatGrams
      portions {
        id
        amount
        unitWithModifier
        weightGrams
      }
      hasAllergies(allergies: $allergies)
      allergies(clientAllergies: $allergies)
    }
  }
`);

const allergenIngredientListItemClientFragment = graphql(`
  fragment AllergenIngredientListItem_Client on Client {
    id
    profile {
      id
      firstName
      gender
    }
  }
`);

const AllergenIngredientListItem: React.FC<Props> = ({
  data,
  client,
  warning,
}) => {
  const { classes, cx } = useStyles();
  const food = data.food;
  const foodAllergies = food?.allergies;

  return (
    <>
      <div className={cx(classes.root, { [classes.rootWarning]: warning })}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap="14px"
        >
          {warning ? <AllergenFoodWarningIcon /> : <AllergenFoodIcon />}
          <Box display="flex" flexDirection="column">
            <Box display="flex" alignItems="center" gap="20px">
              <Typography variant="body1" className={classes.name}>
                {data.food.name}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap="8px">
              {food.portions.map((data, index) => {
                return (
                  <>
                    <Typography className={classes.amount} variant="body2">
                      {data.amount} {data.unitWithModifier}
                    </Typography>
                    {index < food.portions.length - 1 && (
                      <FiberManualRecordIcon
                        className={classes.dotDivider}
                        htmlColor="#595F68"
                      />
                    )}
                  </>
                );
              })}
            </Box>
          </Box>
          {warning && (
            <div className={classes.warning}>
              <div className={classes.warningIndicator}>
                <WarningIcon
                  className={classes.warningLogo}
                  htmlColor={colors.white}
                />
                <Typography className={classes.warningIndicatorText}>
                  Allergen
                </Typography>
              </div>
              <div>
                <Typography
                  className={classes.warningDescription}
                  variant="caption"
                >
                  Oops! {food.name} is an allergen to{" "}
                  {client?.profile?.firstName}. Skip that ingredient in{" "}
                  {client?.profile?.gender == Gender.Female ? "her" : "his"}{" "}
                  meal plan. Trust me, it{`'`}s like mixing oil and water—just
                  not a match made in food heaven!
                </Typography>
                <div className={classes.activeAllergen}>
                  <Typography
                    className={classes.activeAllergenText}
                    variant="body2"
                  >
                    Active allergen:
                  </Typography>
                  <div
                    className={classes.warningIndicator}
                    style={{ marginLeft: 4 }}
                  >
                    {foodAllergies?.map((allergy, index) => (
                      <Typography
                        key={index}
                        className={classes.warningIndicatorText}
                      >
                        {index < foodAllergies.length - 1
                          ? `${humanize(allergy)}, `
                          : humanize(allergy)}
                      </Typography>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <MNRCol>
            <MNValue variant="body2">{food.energyKcal} kcal</MNValue>
            <MNLabel variant="body2">Calories</MNLabel>
          </MNRCol>
          <MNRCol>
            <MNValue variant="body2">{food.carbohydrateGrams} g</MNValue>
            <MNLabel variant="body2">Carbs</MNLabel>
          </MNRCol>
          <MNRCol>
            <MNValue variant="body2">{food.proteinGrams} g</MNValue>
            <MNLabel variant="body2">Protein</MNLabel>
          </MNRCol>
          <MNRCol>
            <MNValue variant="body2">{food.fatGrams} g</MNValue>
            <MNLabel variant="body2">Fat</MNLabel>
          </MNRCol>
        </Box>
      </div>
      {!warning && <div className={classes.divider} />}
    </>
  );
};

export default AllergenIngredientListItem;
